import React, { useState } from "react";
import { Button } from "components/Graphs/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Input } from "components/Graphs/Input";
import { useCreateManagedSecretMutation } from "server/devplane/managedSecrets/mutations";

interface CreateSecretDialogProps {
  open: boolean;
  organizationId: string;
  onOpenChange: (open: boolean) => void;
  onCreated: (secretId: string) => void;
}

export const CreateSecretDialog: React.FC<CreateSecretDialogProps> = ({
  open,
  organizationId,
  onOpenChange,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const createSecret = useCreateManagedSecretMutation();

  const resetForm = () => {
    setName("");
    setValue("");
    setError("");
    createSecret.reset();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!value) {
      setError("Value is required.");
      return;
    }

    setError("");
    try {
      const secret = await createSecret.mutateAsync({
        name: name.trim(),
        value,
        organizationId,
      });
      resetForm();
      onCreated(secret.id);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to create secret.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create secret</DialogTitle>
            <DialogDescription>
              The value is encrypted and becomes version 1. It is not shown in
              the secrets list after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-6">
            <div className="space-y-2">
              <label
                htmlFor="managed-secret-name"
                className="block text-sm font-medium text-primary"
              >
                Name
              </label>
              <Input
                id="managed-secret-name"
                value={name}
                autoComplete="off"
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. HUGGING_FACE_TOKEN"
                disabled={createSecret.isLoading}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="managed-secret-value"
                className="block text-sm font-medium text-primary"
              >
                Secret value
              </label>
              <Input
                id="managed-secret-value"
                type="password"
                value={value}
                autoComplete="new-password"
                onChange={(event) => setValue(event.target.value)}
                placeholder="Enter the secret value"
                disabled={createSecret.isLoading}
              />
              <p className="text-xs text-secondary">
                The console only requests plaintext values when you explicitly
                reveal a version.
              </p>
            </div>
            {error && (
              <p role="alert" className="text-sm text-red-400">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={createSecret.isLoading}
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={createSecret.isLoading}>
              Create secret
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
